/**
 * /employment-coaching/confirmation — polls the coaching checkout status and,
 * once Stripe confirms payment, shows the buyer their next steps (book the
 * 60-minute strategy session, submit CV & LinkedIn, optional free Ditch
 * Careers registration).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Plane, ArrowRight, CalendarClock, FileUp, Compass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 15;

export default function EmploymentCoachingConfirmation() {
  const [phase, setPhase] = useState("checking"); // checking | ready | error
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    const fail = (msg) => { setErrorMsg(msg); setPhase("error"); };

    const pollStatus = async () => {
      if (polls.current >= MAX_POLLS) {
        fail("We couldn't confirm your payment in time. If you were charged, your confirmation and next steps are on their way by email.");
        return;
      }
      polls.current += 1;
      try {
        const { data } = await axios.get(`${API}/coaching/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo({
            firstName: data.first_name,
            strategyUrl: data.strategy_call_url,
            careersUrl: data.careers_register_url,
            submitEmail: data.submit_email,
          });
          setPhase("ready");
        } else if (data.status === "expired") {
          fail("Your checkout session expired before payment was completed. No charge was made — head back and try again.");
        } else {
          setTimeout(pollStatus, 2000);
        }
      } catch {
        fail("Something went wrong while confirming your payment.");
      }
    };

    if (sessionId) pollStatus();
    else fail("This page needs a valid checkout session.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)] flex flex-col" data-testid="coaching-confirmation-page">
      <header className="px-5 md:px-8 py-5">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className={`w-full bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-8 md:p-10 shadow-xl ${phase === "ready" ? "max-w-xl" : "max-w-lg text-center"}`}>
          {phase === "checking" && (
            <>
              <Loader2 size={40} className="mx-auto animate-spin text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6 text-center">Confirming your payment…</h1>
              <p className="text-[var(--ocean)] mt-2 text-sm text-center">Hang tight — this only takes a few seconds.</p>
            </>
          )}

          {phase === "ready" && (
            <>
              <div className="text-center">
                <CheckCircle2 size={44} className="mx-auto text-[var(--ink)]" />
                <span className="inline-block mt-5 text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--ink)] bg-[var(--lime)] px-3 py-1.5 rounded-full">
                  Employment Preparation & Coaching
                </span>
                <h1 className="font-display text-3xl font-extrabold mt-3 leading-tight">
                  You're in{info.firstName ? `, ${info.firstName}` : ""}.
                </h1>
                <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
                  Your 30 days of one-to-one career support starts now. Here's what to do next.
                </p>
              </div>

              <ol className="mt-7 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[var(--lime)] flex items-center justify-center"><CalendarClock size={16} className="text-[var(--ink)]" /></span>
                  <div>
                    <p className="font-bold text-[var(--ink)]">1. Book your 60-minute strategy session</p>
                    <a href={info.strategyUrl} target="_blank" rel="noreferrer" data-testid="coaching-book-strategy"
                      className="mt-2 inline-flex items-center gap-2 py-2.5 px-5 rounded-full bg-[var(--ink)] text-[var(--paper)] font-display font-bold text-sm hover:brightness-110 transition-all">
                      Book my session <ArrowRight size={14} />
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[var(--lime)] flex items-center justify-center"><FileUp size={16} className="text-[var(--ink)]" /></span>
                  <div>
                    <p className="font-bold text-[var(--ink)]">2. Send us your CV & LinkedIn</p>
                    <p className="text-sm text-[var(--ocean)] mt-1">
                      Email your current CV and LinkedIn profile link to{" "}
                      <a href={`mailto:${info.submitEmail}`} className="underline text-[var(--ink)]">{info.submitEmail}</a>{" "}
                      so we can start your rewrite.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[var(--lime)] flex items-center justify-center"><Compass size={16} className="text-[var(--ink)]" /></span>
                  <div>
                    <p className="font-bold text-[var(--ink)]">3. (Optional) Register free with The Ditch Careers</p>
                    <a href={info.careersUrl} target="_blank" rel="noreferrer" data-testid="coaching-register-careers"
                      className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-[var(--ink)] underline underline-offset-4">
                      Register with The Ditch Careers <ArrowRight size={13} />
                    </a>
                  </div>
                </li>
              </ol>

              <p className="mt-7 text-[11px] text-[var(--ocean)] leading-relaxed border-t border-[var(--line)] pt-4">
                This coaching package does not guarantee interviews, job placement or an offer of employment.
                Registering with The Ditch Careers is separate and free of charge.
              </p>
              <a href="/" className="mt-5 inline-block text-sm text-[var(--ocean)] underline">← Back to the site</a>
            </>
          )}

          {phase === "error" && (
            <>
              <AlertCircle size={44} className="mx-auto text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6" data-testid="coaching-confirmation-error">Hmm, that didn't work</h1>
              <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">{errorMsg}</p>
              <p className="text-sm text-[var(--ink)] mt-5">
                Email us at{" "}
                <a href="mailto:hello@theditchcollective.co.nz" className="font-bold underline">hello@theditchcollective.co.nz</a>{" "}
                and we'll sort you out.
              </p>
              <a href="/" className="mt-6 inline-block text-sm text-[var(--ocean)] underline">← Back to the site</a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
